import { createFileRoute, Link } from "@tanstack/react-router";
import { Code } from "../../components/Code";

export const Route = createFileRoute("/docs/how-it-works")({
  component: HowItWorks,
});

function HowItWorks() {
  return (
    <>
      <h1>How it works</h1>
      <p className="lead">
        notify is a thin, hosted layer over the Web Push standard. Here’s what actually happens
        between the three parties.
      </p>

      <h2>The three parties</h2>
      <ul>
        <li>
          <strong>The browser</strong> owns the push subscription and shows the notification. It will
          only register a service worker that is same-origin with the page.
        </li>
        <li>
          <strong>notify</strong> (on Cloudflare’s edge) stores the subscription against your{" "}
          <span className="icode">userId</span>, holds the VAPID keypair, and signs + delivers each
          push.
        </li>
        <li>
          <strong>Your backend</strong> never speaks the push protocol. It calls one endpoint.
        </li>
      </ul>

      <h2>Subscription flow</h2>
      <p>
        When you call <span className="icode">notify.subscribe(userId)</span>, the script:
      </p>
      <ul>
        <li>registers your same-origin service worker (which imports our handler logic);</li>
        <li>requests notification permission;</li>
        <li>
          fetches the public VAPID key from <span className="icode">GET /config</span> (so keys can
          rotate without you re-shipping);
        </li>
        <li>
          creates the <span className="icode">PushSubscription</span> and posts it to{" "}
          <span className="icode">POST /subscribe</span> with your <strong>publishable</strong> key,
          keyed by your <span className="icode">userId</span>.
        </li>
      </ul>
      <p>
        The publishable key is safe in the browser — it can only register devices, never send.
      </p>

      <h2>Why the one-line service worker</h2>
      <p>
        A service worker must be served from the same origin as the page that registers it. Since
        notify.js loads cross-origin, it can’t register our worker directly — so you host a one-liner
        that imports it. Cross-origin <span className="icode">importScripts</span> in a worker is
        allowed, so this is all it takes:
      </p>
      <Code fname="notify-sw.js" badge="your origin" copyText={'importScripts("https://api.getnotify.dev/sw.js");'}>
{`importScripts("https://api.getnotify.dev/sw.js");`}
      </Code>

      <h2>Send flow</h2>
      <p>
        <span className="icode">POST /send</span>, authenticated with your <strong>secret</strong> key
        (server-side only), looks up the subscription for the <span className="icode">userId</span>,
        encrypts the payload (RFC 8291) and signs the VAPID JWT (RFC 8292) using WebCrypto, then
        forwards it to the browser’s push service. Your service worker receives the{" "}
        <span className="icode">push</span> event and shows the notification.
      </p>
      <p>
        Publishable and secret key both belong to one project, so they share the same subscribers —
        but only the secret key can send.
      </p>

      <div className="callout">
        Built on Effect and deployed with Alchemy. Storage is Cloudflare D1; the push encryption runs
        on WebCrypto so it works inside a Worker.
      </div>

      <div className="doc-foot">
        <Link to="/docs/quickstart" className="ilink">
          ← Quickstart
        </Link>
        <Link to="/docs/api-reference" className="ilink">
          API reference →
        </Link>
      </div>
    </>
  );
}
